import { useEffect, useRef } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FieldProps } from '@rjsf/utils';
// eslint-disable-next-line @backstage/no-mixed-plugin-imports
import { usePlatformFeatureFlag } from '@mb.io/plugin-platform-feature-flag';
import { FeatureFlaggedAlertUiOptions } from './schema';

export const FeatureFlaggedAlert = ({
  featureFlag,
  text,
  secondaryText,
  severity = 'error',
  linkText,
  linkUrl,
  sx,
}: FeatureFlaggedAlertUiOptions & { sx?: Record<string, unknown> }) => {
  const feature = usePlatformFeatureFlag(featureFlag);
  const isActive = feature?.available ?? false;

  if (!isActive || !text) {
    return null;
  }

  const isInternalLink = Boolean(linkUrl?.startsWith('/'));

  return (
    <Alert
      severity={severity}
      sx={sx}
      action={
        linkText && linkUrl ? (
          <Link
            href={linkUrl}
            target={isInternalLink ? undefined : '_blank'}
            rel={isInternalLink ? undefined : 'noopener noreferrer'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              whiteSpace: 'nowrap',
              fontWeight: 500,
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {linkText}
            <OpenInNewIcon fontSize="inherit" />
          </Link>
        ) : undefined
      }
    >
      <Box>
        <Typography variant="body2">{text}</Typography>
        {secondaryText && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {secondaryText}
          </Typography>
        )}
      </Box>
    </Alert>
  );
};

export const FeatureFlaggedAlertField = ({
  uiSchema,
  idSchema,
}: FieldProps<string>) => {
  const options = (uiSchema as any)?.['ui:options'] as
    | FeatureFlaggedAlertUiOptions
    | undefined;

  const featureFlag = options?.featureFlag;
  const feature = usePlatformFeatureFlag(featureFlag || 'dummy-flag');
  const isActive = feature?.available ?? false;
  const shouldRender = isActive && Boolean(options?.text);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let el = containerRef.current;
    let depth = 0;
    while (el && el.tagName !== 'FORM' && depth < 10) {
      if (el !== containerRef.current) {
        if (
          typeof el.className === 'string' &&
          el.className.includes('MuiGrid-container')
        )
          break;
        const hasOtherFields = Array.from(
          el.querySelectorAll('[id^="root_"]'),
        ).some(
          node =>
            idSchema?.$id &&
            node.id !== idSchema.$id &&
            !node.id.startsWith(`${idSchema.$id}_`),
        );
        if (hasOtherFields) break;
      }

      if (shouldRender) {
        const currentEl = el;
        ['display', 'margin', 'padding', 'height', 'border'].forEach(p =>
          currentEl!.style.removeProperty(p),
        );
      } else {
        const currentEl = el;
        currentEl.style.setProperty('display', 'none', 'important');
        ['margin', 'padding', 'height'].forEach(p =>
          currentEl!.style.setProperty(p, '0', 'important'),
        );
        currentEl.style.setProperty('border', 'none', 'important');
      }

      el = el.parentElement as HTMLDivElement | null;
      depth++;
    }
  }, [shouldRender, idSchema?.$id]);

  if (!shouldRender) {
    return (
      <div ref={containerRef} id={idSchema?.$id} style={{ display: 'none' }} />
    );
  }

  return (
    <div ref={containerRef} id={idSchema?.$id}>
      {options ? <FeatureFlaggedAlert {...options} sx={{ mb: 2 }} /> : null}
    </div>
  );
};
